(function () {
  const viewTransitionRenderBlocker = document.getElementById('view-transition-render-blocker');
  // Remove the view transition render blocker if the user has reduced motion enabled or is on a low power device.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || isLowPowerDevice()) {
    viewTransitionRenderBlocker?.remove();
  } else {
    // If the browser didn't manage to parse the main content quickly, at least let the user see something.
    // We're aiming for the FCP to be under 1.8 seconds since the navigation started.
    const RENDER_BLOCKER_TIMEOUT_MS = Math.max(0, 1800 - performance.now());

    setTimeout(() => {
      viewTransitionRenderBlocker?.remove();
    }, RENDER_BLOCKER_TIMEOUT_MS);
  }

  const idleCallback = typeof requestIdleCallback === 'function' ? requestIdleCallback : setTimeout;

  window.addEventListener('pageswap', (event) => {
    try {
      handlePageSwap(event);
    } catch (error) {
      // Enhancement only — never block native navigation.
    }
  });

  window.addEventListener('pagereveal', (event) => {
    try {
      handlePageReveal(event);
    } catch (error) {
      // Enhancement only — never block native navigation.
    }
  });

  /**
   * @param {Event} event
   */
  function handlePageSwap(event) {
    const viewTransition = getEventViewTransition(event);

    if (shouldSkipViewTransition(viewTransition)) {
      skipTransitionSafe(viewTransition);
      return;
    }

    // Cancel view transition on user interaction to improve INP (Interaction to Next Paint)
    ['pointerdown', 'keydown'].forEach((eventName) => {
      document.addEventListener(
        eventName,
        () => {
          skipTransitionSafe(viewTransition);
        },
        { once: true }
      );
    });

    // Clean in case you landed on the pdp first. We want to remove the default transition type on the PDP media gallery so there is no duplicate transition name
    document
      .querySelectorAll('[data-view-transition-type]:not([data-view-transition-triggered])')
      .forEach((element) => {
        element.removeAttribute('data-view-transition-type');
      });

    const transitionTriggered = document.querySelector('[data-view-transition-triggered]');
    const transitionType = transitionTriggered?.getAttribute('data-view-transition-type');

    if (!hasTransitionTypes(viewTransition)) return;

    if (transitionType) {
      viewTransition.types.clear();
      viewTransition.types.add(transitionType);
      setSessionItem('custom-transition-type', transitionType);
    } else {
      viewTransition.types.clear();
      viewTransition.types.add('page-navigation');
      removeSessionItem('custom-transition-type');
    }
  }

  /**
   * @param {Event} event
   */
  function handlePageReveal(event) {
    const viewTransition = getEventViewTransition(event);

    if (shouldSkipViewTransition(viewTransition)) {
      skipTransitionSafe(viewTransition);
      return;
    }

    const customTransitionType = getSessionItem('custom-transition-type');

    if (!hasTransitionTypes(viewTransition)) return;

    if (customTransitionType) {
      viewTransition.types.clear();
      viewTransition.types.add(customTransitionType);

      const finished = viewTransition.finished;
      if (finished && typeof finished.then === 'function') {
        finished
          .then(() => {
            if (!hasTransitionTypes(viewTransition)) return;
            viewTransition.types.clear();
            viewTransition.types.add('page-navigation');

            idleCallback(() => {
              removeSessionItem('custom-transition-type');
              document.querySelectorAll('[data-view-transition-type]').forEach((element) => {
                element.removeAttribute('data-view-transition-type');
              });
            });
          })
          .catch(() => {});
      }
    } else {
      viewTransition.types.clear();
      viewTransition.types.add('page-navigation');
    }
  }

  /**
   * @param {Event | null | undefined} event
   * @returns {unknown}
   */
  function getEventViewTransition(event) {
    if (!event || typeof event !== 'object') return null;
    return /** @type {{ viewTransition?: unknown }} */ (event).viewTransition;
  }

  /**
   * @returns {boolean}
   */
  function hasViewTransitionConstructor() {
    return typeof ViewTransition === 'function';
  }

  /**
   * @param {unknown} viewTransition
   * @returns {boolean}
   */
  function isUsableViewTransition(viewTransition) {
    if (!hasViewTransitionConstructor() || viewTransition == null) return false;
    try {
      return viewTransition instanceof ViewTransition;
    } catch (error) {
      return false;
    }
  }

  /**
   * @param {unknown} viewTransition
   * @returns {boolean}
   */
  function hasTransitionTypes(viewTransition) {
    const types = viewTransition && /** @type {{ types?: { clear?: unknown, add?: unknown } }} */ (viewTransition).types;
    return !!(types && typeof types.clear === 'function' && typeof types.add === 'function');
  }

  /**
   * @param {unknown} viewTransition
   */
  function skipTransitionSafe(viewTransition) {
    if (!viewTransition || typeof viewTransition.skipTransition !== 'function') return;
    try {
      viewTransition.skipTransition();
    } catch (error) {
      // Ignore unsupported or already-finished transitions.
    }
  }

  /**
   * @param {unknown} viewTransition
   * @returns {boolean}
   */
  function shouldSkipViewTransition(viewTransition) {
    return !isUsableViewTransition(viewTransition) || isLowPowerDevice();
  }

  /**
   * @param {string} key
   * @returns {string | null}
   */
  function getSessionItem(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  /**
   * @param {string} key
   * @param {string} value
   */
  function setSessionItem(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (error) {
      // Private mode / blocked storage must not break navigation.
    }
  }

  /**
   * @param {string} key
   */
  function removeSessionItem(key) {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      // Private mode / blocked storage must not break navigation.
    }
  }

  /*
   * We can't import this logic from utilities.js here, but we should keep them in sync.
   */
  function isLowPowerDevice() {
    /* Skip ESLint compatibility check. Number(undefined) <= 2 is always false anyway. */
    /* eslint-disable-next-line compat/compat */
    return Number(navigator.hardwareConcurrency) <= 2 || Number(navigator.deviceMemory) <= 2;
  }
})();
